import { computed } from "vue";

import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  CHECKOUT_NEW_ID,
  CHECKOUT_TEMPLATE_SCHEMA_VERSION,
} from "@/helpers/constants/checkout";
import { useFilters } from "@/hooks/filters";
import applicationsApiService from "@/services/modules/applications";
import checkoutTemplateService from "@/services/modules/checkoutTemplates";

import type {
  CheckoutTemplate,
  UpdateCheckoutSnapshotParams,
} from "@/models/checkout";
import type { IOffer } from "@/models/funders";
import type { Ref } from "vue";

export const CHECKOUT_QUERY_KEYS = {
  TEMPLATES_INDEX: "checkout-templates-index",
  TEMPLATE: "checkout-template",
  SNAPSHOT: "checkout-snapshot",
  OPTIONS: "checkout-options",
} as const;

export const useCheckoutTemplateIndexQuery = () => {
  const { filterState, filterStateAsSearchParams } = useFilters();

  return useQuery({
    queryKey: [CHECKOUT_QUERY_KEYS.TEMPLATES_INDEX, filterState],
    queryFn: async () => {
      return await checkoutTemplateService.getAll(
        filterStateAsSearchParams.value
      );
    },
    staleTime: Infinity,
  });
};

export const useCheckoutTemplateQuery = (
  templateId: Ref<CheckoutTemplate["id"]>
) => {
  const queryEnabled = computed(
    () => !!templateId.value && templateId.value !== CHECKOUT_NEW_ID
  );

  return useQuery({
    queryKey: [CHECKOUT_QUERY_KEYS.TEMPLATE, templateId],
    queryFn: async () => {
      return await checkoutTemplateService.getById(templateId.value!);
    },
    enabled: queryEnabled,
    staleTime: Infinity,
  });
};

export const useCheckoutSnapshotQuery = (
  offerId: Ref<IOffer["id"] | undefined>
) => {
  return useQuery({
    queryKey: [CHECKOUT_QUERY_KEYS.SNAPSHOT, offerId],
    queryFn: async () => {
      return await applicationsApiService.getCheckoutSnapshot(offerId.value!);
    },
    enabled: computed(() => Boolean(offerId?.value)),
  });
};

export const useCreateCheckoutMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (template: CheckoutTemplate) => {
      template.content.version = CHECKOUT_TEMPLATE_SCHEMA_VERSION;

      const newTemplate = await checkoutTemplateService.create(template);
      queryClient.invalidateQueries({
        queryKey: [CHECKOUT_QUERY_KEYS.TEMPLATES_INDEX],
      });

      return newTemplate;
    },
  });
};

export const useUpdateCheckoutMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (template: CheckoutTemplate & { id: string }) => {
      const updatedTemplate = await checkoutTemplateService.update(template);
      queryClient.invalidateQueries({
        queryKey: [CHECKOUT_QUERY_KEYS.TEMPLATES_INDEX],
      });

      return updatedTemplate;
    },
  });
};

export const useUpdateCheckoutSnapshotMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      updateCheckoutSnapshotParams: UpdateCheckoutSnapshotParams
    ) => {
      const updatedTemplate =
        await applicationsApiService.updateCheckoutSnapshot(
          updateCheckoutSnapshotParams
        );
      queryClient.invalidateQueries({
        queryKey: [
          CHECKOUT_QUERY_KEYS.SNAPSHOT,
          updateCheckoutSnapshotParams.offerId,
        ],
      });

      return updatedTemplate;
    },
  });
};

export const useDeleteCheckoutTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await checkoutTemplateService.deleteById(id);
      queryClient.invalidateQueries({
        queryKey: [CHECKOUT_QUERY_KEYS.TEMPLATES_INDEX],
      });
    },
  });
};
